import { Button, Grid, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import * as Yup from 'yup';
import DownloadIcon from '@mui/icons-material/Download';

import { SiTrackPage } from "../../components/si-track-page";
import { SiTrackGrid } from "../../components/si-track-grid/si-track-grid";
import { fastFieldReprocessQueueColumns } from "./fast-field-reporcess-queue-columns";
import { useLoadData } from "../../hooks/useLoadData";
import { ApiResponse } from "../../models/api-response";
import { GridColumnConfig, GridPreference } from "../../models/gridCoumnConfig";
import { gridApiKeyStore } from "../../api/grid-api-key-store";
import { useSaveData } from "../../hooks/useSaveData";
import { useEffect, useState } from "react";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../hooks/use-debounce";
import { UserPermissionName } from "../../models/user-permission-name";
import { workOrdersApiKeyStore } from "../../api/workorders-api-key-store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SaveHookFormDialog } from "../../components/save-hook-form.dialog";
import { FastFieldQueueForm } from "./fast-field-queue.form";
import { ReactHookForm } from "../../components/hook-form/react-hook-form.component";
import { SelectResult } from "../../components/select/select-chain.component";
import { useReactHookForm } from "../../hooks/useReactHookForm";
import { FastFieldQueueFilters } from "./fast-field-reprocess-queue.filters";
import { FileDownloadDialog } from "./file-download.dialog";

const styling = {
    paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
    field: string;
}

interface CellProps {
    workorder: any;
}

const fastFieldQueueFormSchema = Yup.object().shape({
    reinspectionwo: Yup.string().required('Workorder number is required'),
});

const getFiltesFromUrl = (params: URLSearchParams) => {
    return {
        customerID: params.get('customerID'),
        officeID: params.get('officeID'),
        projectID: params.get('projectID'),
        unitID: params.get('unitID'),
    };
};

export const FastFieldReprocessQueuePage = () => {

    const navigate = useNavigate();

    const [editItem, setEditItem] = useState<Partial<any>>();
    const [searchParams] = useSearchParams();
    const [openFileDownloadDo, setFileDownloadDialog] = useState<boolean>(false);
    const [dialogQueueID, setDialogQueueID] = useState<any>();
    const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));

    const form = useReactHookForm({
        defaultValues: {
            customer: { customerID: '', customerName: '' },
            office: { officeID: '', officeName: '' },
            project: { projectID: '', projectName: '' },
            unit: { unitID: '', unitName: '' },
        },
    });

    const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
        workOrdersApiKeyStore.getFastFieldReprocessQueue(selectedFilters),
        !!selectedFilters?.customerID ||
        !!selectedFilters?.officeID ||
        !!selectedFilters?.projectID ||
        !!selectedFilters?.unitID
    );

    useEffect(() => {
        if (selectedFilters?.customerID == '')
            refetch();
    }, [selectedFilters])

    const handleSubmit = (filtersData: any) => {
        const filteringData = {
            customerID: filtersData?.customer?.customerID ?? '',
            officeID: filtersData?.office?.officeID ?? "",
            projectID: filtersData?.project?.projectID ?? "",
            unitID: filtersData?.unit?.unitID ?? "",
        };
        setSelectedFilters(filteringData);
    };

    const handleEditFastFieldQueue = (fastFieldQueue: any) => {
        setEditItem({ queueID: fastFieldQueue?.queueID });
    };

    const handleEditClose = () => {
        setEditItem(undefined);
    };

    const handleCreateFastFieldQueue = () => {
        setEditItem({});
    };

    const ReferenceWOCell = ({ workorder }: CellProps) => {
        const [isHovered, setIsHovered] = useState(false);
        const handleClickWorkorderNumber = () => {
            navigate(`/workorders?searchType=workorders&searchText=${workorder?.referenceWOFF}`);
        };
        return (
            <td style={{ cursor: 'pointer' }} onClick={handleClickWorkorderNumber}>
                <Typography
                    component="span"
                    style={{
                        color: isHovered ? '#0000b5' : '#0000b5',
                        textDecoration: isHovered ? 'underline' : 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {workorder?.referenceWOFF}
                </Typography>
            </td>
        );
    };

    const ReinspectWOCell = ({ workorder }: CellProps) => {
        const [isHovered, setIsHovered] = useState(false);
        const handleClickWorkorderNumber = () => {
            navigate(`/workorders?searchType=workorders&searchText=${workorder?.reinspectionWO}`);
        };
        return (
            <td style={{ cursor: 'pointer' }} onClick={handleClickWorkorderNumber}>
                <Typography
                    component="span"
                    style={{
                        color: isHovered ? '#0000b5' : '#0000b5',
                        textDecoration: isHovered ? 'underline' : 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {workorder?.reinspectionWO}
                </Typography>
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
        if (field === 'referenceWOFF') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <ReferenceWOCell workorder={dataItem.dataItem} />;
            };
        }
        if (field === 'reinspectionWO') {
            // Return a function that takes a data item and returns a JSX element
            return (dataItem: any) => {
                return <ReinspectWOCell workorder={dataItem.dataItem} />;
            };
        }

        return undefined;
    };

    const handleViewDownload = (queueID: any, workOrderNumber: any) => {
        const passData = {
            queueID: queueID,
            workOrderNumber: workOrderNumber
        }
        setFileDownloadDialog(true);
        setDialogQueueID(passData);
    }

    const viewDownloadElement = (dataItem: any) => {
        return (
            <>
                {/* <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MOVE_CONTACT]}> */}
                <IconButton aria-label="move" onClick={() => handleViewDownload(dataItem?.dataItem?.queueID, dataItem?.dataItem?.reinspectionWO)}>
                    <Tooltip title={<Typography variant="body1">Download</Typography>}>
                        <DownloadIcon className="btnEdit" />
                    </Tooltip>
                </IconButton>
                {/* </GuardedComponent> */}
            </>
        );
    }

    const handleClosedDilaog = () => {
        setDialogQueueID(undefined);
        setFileDownloadDialog(false);
    }

    // -------------------------------------------------Grid Layout------------------------------

    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout('FastFieldQueue', 'FastFieldQueue')
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredFastFieldQueue, setFilteredFastFieldQueue] = useState<any[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredFastFieldQueueList = data?.data?.filter((FastFieldQueue: any) =>
            Object.values(FastFieldQueue).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredFastFieldQueue(filteredFastFieldQueueList || []);
    }, [debouncedSearchTerm, data?.data]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: 'FastFieldQueue',
            pageName: 'FastFieldQueue',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: 'FastFieldQueue',
            pageName: 'FastFieldQueue',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    return (
        <SiTrackPage title="Reinspection Exception Report">
            <ReactHookForm formName="WORK_ORDER_INSPECTION_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
                <Grid container spacing={1}>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <FastFieldQueueFilters />
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                        <Paper elevation={0} style={styling.paperStyle}>
                            <SiTrackGrid
                                fileTitle='reinspection_exception'
                                columns={fastFieldReprocessQueueColumns}
                                dataItemKey="queueID"
                                // customCell={(field) =>
                                //     CustomGridCell({
                                //         field,
                                //     })
                                // }
                                CustomComponent={viewDownloadElement}
                                onEdit={handleEditFastFieldQueue}
                                actionPermissions={{
                                    edit: UserPermissionName.EDIT_REINSPECTION_EXCEPTION_REPORT,
                                    export: UserPermissionName.DOWNLOAD_GRID,
                                }}
                                isBusy={isLoading || isFetching || isLoadingGridLayout}

                                // -----------------Grid Layout-------
                                dataItems={filteredFastFieldQueue || []}
                                reorderable
                                handleSearch={handleInputChange}
                                handleSaveLayout={handleSaveLayout}
                                isSaveLayout={true}
                                gridConfigFromApi={gridConfig}
                                handleResetLayout={handleResetLayout}
                            // -----------------Grid Layout-------
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </ReactHookForm>
            {editItem && (
                <SaveHookFormDialog
                    title={editItem.queueID ?
                        <Typography variant="body1" style={{ fontSize: 18, fontWeight: 500 }}>UPDATE FASTFIELD REINSPECTION WORKORDER NUMBER</Typography>
                        : <Typography variant="body1" style={{ fontSize: 18, fontWeight: 500 }}>CREATE FASTFIELD REINSPECTION WORKORDER NUMBER</Typography>}
                    config={{
                        saveConfig: editItem.queueID
                            ? workOrdersApiKeyStore.startFastFieldReprocessQueue()
                            : workOrdersApiKeyStore.startFastFieldReprocessQueue(),
                        invalidateRoute: () => workOrdersApiKeyStore.getFastFieldReprocessQueue(selectedFilters),
                    }}
                    data={editItem}
                    yupSchema={fastFieldQueueFormSchema}
                    onClose={handleEditClose}
                    size="sm"
                >
                    <FastFieldQueueForm editItem={editItem} />
                </SaveHookFormDialog>
            )}
            {openFileDownloadDo && (
                <FileDownloadDialog queueData={dialogQueueID} onClosed={handleClosedDilaog} />
            )}
        </SiTrackPage>
    );
}